// amount of video chunks for coverage tracking calculation
const COVERAGE_BATCH_COUNT = 100

// if current time increased more than this value, it's considered as skipped(in case of navigation/seek)
const SKIPPED_TIME_THRESHOLD_SEC = 1

// if two ranges are closer than this value, they are merged
const RANGES_MERGE_GAP_SEC = 0.25

export class CoverageTracker {
  #ranges = []
  #coveredBatches = Array(COVERAGE_BATCH_COUNT).fill(0) // 0/1
  #coveredCount = 0
  #playStartSec = null
  #prevTimeSec = 0

  #setBatch(idx) {
    if (idx < 0 || idx >= COVERAGE_BATCH_COUNT) return
    if (this.#coveredBatches[idx] === 0) {
      this.#coveredBatches[idx] = 1
      this.#coveredCount++
    }
  }

  #coverRange({ videoDurationSec, startSec, endSec }) {
    if (!videoDurationSec) return

    let start = Math.max(0, Math.min(startSec, videoDurationSec))
    let end = Math.max(0, Math.min(endSec, videoDurationSec))
    if (end <= start) return

    const startBatch = Math.floor((start / videoDurationSec) * COVERAGE_BATCH_COUNT)
    const endBatchEx = Math.min(COVERAGE_BATCH_COUNT, Math.ceil((end / videoDurationSec) * COVERAGE_BATCH_COUNT))
    for (let i = startBatch; i < endBatchEx; i++) {
      this.#setBatch(i)
    }
  }

  #mergeRanges() {
    this.#ranges.sort((a, b) => a[0] - b[0])
    const out = []
    for (const [s, e] of this.#ranges) {
      if (!out.length) {
        out.push([s, e])
        continue
      }
      const last = out[out.length - 1]
      if (s <= last[1] + RANGES_MERGE_GAP_SEC) {
        last[1] = Math.max(last[1], e)
      } else {
        out.push([s, e])
      }
    }
    this.#ranges = out
  }

  #ingestRanges({ videoDurationSec }) {
    if (!videoDurationSec || !this.#ranges) return

    this.#ranges.forEach(([startSec, endSec]) => {
      this.#coverRange({ videoDurationSec, startSec, endSec })
    })
  }

  #isNavigated({ currentTimeSec }) {
    if (typeof this.#prevTimeSec !== 'number') return false

    return Math.abs(currentTimeSec - this.#prevTimeSec) > SKIPPED_TIME_THRESHOLD_SEC
  }

  #processRange({ endSec, videoDurationSec }) {
    if (this.#playStartSec === null) return
    if (!videoDurationSec || typeof endSec !== 'number') {
      this.#playStartSec = null
      return
    }

    if (endSec > this.#playStartSec + 0.01) {
      this.#ranges.push([this.#playStartSec, endSec])
      this.#mergeRanges()
      this.#ingestRanges({ videoDurationSec })
    }

    this.#playStartSec = null
  }

  startRange({ startSec }) {
    if (this.#playStartSec !== null) return
    this.#prevTimeSec = startSec
    this.#playStartSec = startSec
  }

  closeRange({ videoDurationSec, currentTimeSec }) {
    if (!videoDurationSec || typeof currentTimeSec !== 'number') {
      this.#playStartSec = null
      return
    }

    if (this.#playStartSec === null) return

    if (this.#isNavigated({ currentTimeSec })) {
      this.#processRange({ endSec: this.#prevTimeSec, videoDurationSec })
    } else {
      this.#processRange({ endSec: currentTimeSec, videoDurationSec })
    }

    this.#prevTimeSec = currentTimeSec
  }

  trackTimeChange({ currentTimeSec, videoDurationSec, isPaused }) {
    if (!isPaused && this.#isNavigated({ currentTimeSec })) {
      this.#processRange({ videoDurationSec, endSec: this.#prevTimeSec })
      this.startRange({ startSec: currentTimeSec })
    }
    this.#prevTimeSec = currentTimeSec
  }

  get coverage() {
    return {
      coverageRate: this.#coveredCount / COVERAGE_BATCH_COUNT,
      coveredBatches: this.#coveredBatches,
    }
  }
}
